import { useMemo } from "react";
import { useFormik } from "formik";
import { Flex, FormControl, FormLabel, Select as ChakraSelect, Input as ChakraInput, Button } from "@chakra-ui/react";
import { RootState, useAppSelector } from "@/store";
import { Customer } from "@/model/customers";
import { Project } from "@/model/projects";
import { VacanciesFilter } from "@/model/vacancy";
import { Recruiter } from "@/model/recruiters";
import { defaultVacanciesFilterFiedls } from "../_helpers";

export interface VacanciesPopupFilterProps {
  readonly values: VacanciesFilter;
  readonly onApply: (values: VacanciesFilter) => void;
  readonly onReset: () => void;
  readonly customers: readonly Customer[];
  readonly allProjects: readonly Project[];
  readonly recruiters: readonly Recruiter[];
}

export const VacanciesPopupFilter = ({ values, onApply, onReset, customers, allProjects, recruiters }: VacanciesPopupFilterProps) => {
  const { guides } = useAppSelector((state: RootState) => state.guides);

  const formik = useFormik<VacanciesFilter>({
    initialValues: values,
    enableReinitialize: true,
    onSubmit: (submittedValues: VacanciesFilter) => {
      onApply(submittedValues);
    },
  });
  const projects = useMemo(() => {
    if (!formik.values.customer_id) return allProjects as Project[];
    const customerId = formik.values.customer_id;
    return (allProjects as Project[]).filter((p) => p.customer?.id === customerId);
  }, [allProjects, formik.values.customer_id]);

  const statusOptions = useMemo(() => {
    const map: Record<string, string> | undefined = (guides as any)?.VacancyStatus;
    if (!map) return [] as { value: string; label: string }[];
    return Object.entries(map).map(([value, label]) => ({ value, label: String(label) }));
  }, [guides]);

  const handleResetLocal = () => {
    formik.resetForm({ values: defaultVacanciesFilterFiedls });
    onReset();
  };

  const handleApplyLocal = () => {
    void formik.submitForm();
  };

  return (
    <>
      <FormControl>
        <FormLabel>Заказчик</FormLabel>
        <ChakraSelect
          placeholder="Все заказчики"
          value={formik.values.customer_id ?? ""}
          onChange={(e) => {
            const nextValue = e.target.value;
            formik.setFieldValue("customer_id", nextValue ? Number(nextValue) : undefined);
            formik.setFieldValue("project_id", undefined);
          }}
        >
          {customers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </ChakraSelect>
      </FormControl>

      <FormControl>
        <FormLabel>Проект</FormLabel>
        <ChakraSelect
          placeholder="Все проекты"
          value={formik.values.project_id ?? ""}
          onChange={(e) => {
            const nextValue = e.target.value;
            formik.setFieldValue("project_id", nextValue ? Number(nextValue) : undefined);
          }}
        >
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </ChakraSelect>
      </FormControl>

      <FormControl>
        <FormLabel>Статус вакансии</FormLabel>
        <ChakraSelect
          placeholder="Все статусы"
          value={formik.values.status || ""}
          onChange={(e) => {
            formik.setFieldValue("status", e.target.value || "");
          }}
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </ChakraSelect>
      </FormControl>

      <FormControl>
        <FormLabel>Назначенный рекрутер</FormLabel>
        <ChakraSelect
          placeholder="Все рекрутеры"
          value={formik.values.recruiter_id ?? ""}
          onChange={(e) => {
            const nextValue = e.target.value;
            formik.setFieldValue("recruiter_id", nextValue ? Number(nextValue) : undefined);
          }}
        >
          {recruiters.map((r) => (
            <option key={r.id} value={r.id}>
              {r.username}
            </option>
          ))}
        </ChakraSelect>
      </FormControl>

      <FormControl>
        <FormLabel>Дата создания</FormLabel>
        <Flex direction="row" gap="12px">
          <ChakraInput
            type="date"
            value={formik.values.created_at_from || ""}
            placeholder="От"
            onChange={(e) => formik.setFieldValue("created_at_from", e.target.value || "")}
          />
          <ChakraInput
            type="date"
            value={formik.values.created_at_to || ""}
            placeholder="До"
            onChange={(e) => formik.setFieldValue("created_at_to", e.target.value || "")}
          />
        </Flex>
      </FormControl>
      <Flex justifyContent="flex-end" gap="8px">
        <Button variant="ghost" onClick={handleResetLocal}>
          Сбросить
        </Button>
        <Button onClick={handleApplyLocal} bg="#2563eb" color="white" _hover={{ bg: "#2563eb" }} _active={{ bg: "#2563eb" }}>
          Применить
        </Button>
      </Flex>
    </>
  );
};
